import { useState } from 'react'
import { Link2, Copy, ExternalLink, Sparkles } from 'lucide-react'

interface UTMParams {
  baseUrl: string
  source: string
  medium: string
  campaign: string
  content: string
  term: string
}

// Get environment-aware URLs
const FAISS_BASE_URL = import.meta.env.VITE_FAISS_BASE_URL || 'https://awssolutionfinder.solutions.cloudnestle.com'
const MARKETPLACE_BASE_URL = window.location.origin

const templates = {
  email: {
    baseUrl: `${FAISS_BASE_URL}/`,
    source: 'email',
    medium: 'campaign',
    campaign: 'weekly-newsletter',
    content: 'cta-button',
    term: ''
  },
  linkedin: {
    baseUrl: `${FAISS_BASE_URL}/`,
    source: 'linkedin',
    medium: 'social',
    campaign: 'product-launch',
    content: 'founder-post',
    term: ''
  },
  twitter: {
    baseUrl: `${FAISS_BASE_URL}/`,
    source: 'twitter',
    medium: 'social',
    campaign: 'product-launch',
    content: 'thread-1',
    term: ''
  },
  google: {
    baseUrl: `${FAISS_BASE_URL}/`,
    source: 'google',
    medium: 'cpc',
    campaign: 'aws-solutions-search',
    content: 'ad-variant-a',
    term: 'aws-sample-code'
  },
  blog: {
    baseUrl: `${FAISS_BASE_URL}/`,
    source: 'blog',
    medium: 'content',
    campaign: 'aws-tutorial',
    content: 'inline-cta',
    term: ''
  },
  partner: {
    baseUrl: `${FAISS_BASE_URL}/`,
    source: 'partner-name',
    medium: 'referral',
    campaign: 'partner-program',
    content: 'homepage-banner',
    term: ''
  }
}

export function UTMGenerator() {
  const [params, setParams] = useState<UTMParams>({
    baseUrl: `${FAISS_BASE_URL}/`,
    source: '',
    medium: '',
    campaign: '',
    content: '',
    term: ''
  })
  const [generatedUrl, setGeneratedUrl] = useState('')
  const [shortUrl, setShortUrl] = useState('')
  const [copied, setCopied] = useState(false)
  const [shortening, setShortening] = useState(false)

  const generateUrl = () => {
    try {
      const url = new URL(params.baseUrl)
      url.searchParams.set('utm_source', params.source)
      url.searchParams.set('utm_medium', params.medium)
      url.searchParams.set('utm_campaign', params.campaign)
      if (params.content) url.searchParams.set('utm_content', params.content)
      if (params.term) url.searchParams.set('utm_term', params.term)
      
      setGeneratedUrl(url.toString())
      setShortUrl('') // Reset short URL when generating new
    } catch (error) {
      alert('Invalid URL format')
    }
  }

  const shortenUrl = async () => {
    if (!generatedUrl) return
    
    setShortening(true)
    try {
      // Using TinyURL API (free, no API key required)
      const response = await fetch(`https://tinyurl.com/api-create.php?url=${encodeURIComponent(generatedUrl)}`)
      const short = await response.text()
      setShortUrl(short)
    } catch (error) {
      console.error('Failed to shorten URL:', error)
      alert('Failed to shorten URL. You can use bit.ly manually.')
    } finally {
      setShortening(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const loadTemplate = (template: keyof typeof templates) => {
    setParams(templates[template])
    setGeneratedUrl('')
    setShortUrl('')
  }

  const QuickButton = ({ field, value }: { field: keyof UTMParams, value: string }) => (
    <button
      type="button"
      onClick={() => setParams({ ...params, [field]: value })}
      className="px-3 py-1 text-xs bg-gray-100 hover:bg-blue-100 hover:text-blue-700 rounded-md transition-colors"
    >
      {value}
    </button>
  )

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold flex items-center">
          <Link2 className="h-5 w-5 mr-2 text-blue-600" />
          UTM Link Generator
        </h2>
      </div>

      {/* Quick Templates */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-center mb-2">
          <Sparkles className="h-4 w-4 mr-2 text-yellow-600" />
          <h3 className="text-sm font-semibold text-yellow-800">Quick Templates</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => loadTemplate('email')} className="px-3 py-1 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors">
            📧 Email Campaign
          </button>
          <button onClick={() => loadTemplate('linkedin')} className="px-3 py-1 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors">
            💼 LinkedIn Post
          </button>
          <button onClick={() => loadTemplate('twitter')} className="px-3 py-1 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors">
            🐦 Twitter Post
          </button>
          <button onClick={() => loadTemplate('google')} className="px-3 py-1 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors">
            🔍 Google Ads
          </button>
          <button onClick={() => loadTemplate('blog')} className="px-3 py-1 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors">
            📝 Blog CTA
          </button>
          <button onClick={() => loadTemplate('partner')} className="px-3 py-1 bg-white border border-yellow-300 rounded-md text-sm hover:bg-yellow-100 transition-colors">
            🤝 Partner Link
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* Base URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Base URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={params.baseUrl}
            onChange={(e) => setParams({ ...params, baseUrl: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={`${FAISS_BASE_URL}/`}
          />
        </div>

        {/* Source */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Source <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={params.source}
            onChange={(e) => setParams({ ...params, source: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
            placeholder="linkedin"
          />
          <div className="flex flex-wrap gap-2">
            <QuickButton field="source" value="linkedin" />
            <QuickButton field="source" value="twitter" />
            <QuickButton field="source" value="email" />
            <QuickButton field="source" value="google" />
            <QuickButton field="source" value="facebook" />
          </div>
          <p className="text-xs text-gray-500 mt-1">Where traffic comes from</p>
        </div>

        {/* Medium */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Medium <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={params.medium}
            onChange={(e) => setParams({ ...params, medium: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
            placeholder="social"
          />
          <div className="flex flex-wrap gap-2">
            <QuickButton field="medium" value="social" />
            <QuickButton field="medium" value="email" />
            <QuickButton field="medium" value="cpc" />
            <QuickButton field="medium" value="referral" />
            <QuickButton field="medium" value="organic" />
          </div>
          <p className="text-xs text-gray-500 mt-1">How they found you</p>
        </div>

        {/* Campaign */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Campaign <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={params.campaign}
            onChange={(e) => setParams({ ...params, campaign: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="product-launch"
          />
          <p className="text-xs text-gray-500 mt-1">Campaign name</p>
        </div>

        {/* Content (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content <span className="text-gray-400 text-xs">(optional - for A/B testing)</span>
          </label>
          <input
            type="text"
            value={params.content}
            onChange={(e) => setParams({ ...params, content: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
            placeholder="button-a"
          />
          <div className="flex flex-wrap gap-2">
            <QuickButton field="content" value="button-a" />
            <QuickButton field="content" value="button-b" />
            <QuickButton field="content" value="hero-banner" />
            <QuickButton field="content" value="sidebar" />
          </div>
          <p className="text-xs text-gray-500 mt-1">Content variant for A/B testing</p>
        </div>

        {/* Term (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Term <span className="text-gray-400 text-xs">(optional - for paid search)</span>
          </label>
          <input
            type="text"
            value={params.term}
            onChange={(e) => setParams({ ...params, term: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="aws-solutions"
          />
          <p className="text-xs text-gray-500 mt-1">Search keywords</p>
        </div>

        {/* Generate Button */}
        <button
          onClick={generateUrl}
          disabled={!params.baseUrl || !params.source || !params.medium || !params.campaign}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
        >
          🚀 Generate UTM Link
        </button>
      </div>

      {/* Result */}
      {generatedUrl && (
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">✅ Your UTM Link:</h3>
            <button
              onClick={() => window.open(generatedUrl, '_blank')}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Test
            </button>
          </div>
          
          <div className="bg-white p-3 rounded border border-gray-300 mb-3 break-all text-sm font-mono text-gray-700">
            {generatedUrl}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => copyToClipboard(generatedUrl)}
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center"
            >
              <Copy className="h-4 w-4 mr-2" />
              {copied ? '✓ Copied!' : 'Copy Link'}
            </button>
            
            <button
              onClick={shortenUrl}
              disabled={shortening}
              className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-md hover:bg-orange-700 disabled:bg-gray-400 transition-colors text-sm font-medium"
            >
              {shortening ? 'Shortening...' : '🔗 Shorten URL'}
            </button>
          </div>

          {/* Short URL Result */}
          {shortUrl && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-green-800 mb-2">✨ Shortened URL:</h4>
              <div className="bg-white p-2 rounded border border-green-300 mb-2 break-all text-sm font-mono text-gray-700">
                {shortUrl}
              </div>
              <button
                onClick={() => copyToClipboard(shortUrl)}
                className="w-full bg-green-600 text-white py-1.5 px-3 rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
              >
                📋 Copy Short Link
              </button>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>💡 Pro Tip:</strong> Use shortened URLs for social media posts. The full UTM parameters are preserved!
            </p>
          </div>

          {/* Secondary Link - Direct Registration */}
          <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-purple-800 mb-2">🎯 Secondary Link (Direct Registration):</h4>
            <p className="text-xs text-purple-700 mb-3">
              For users who are already convinced and want to skip the landing page:
            </p>
            <div className="bg-white p-3 rounded border border-purple-300 mb-3 break-all text-sm font-mono text-gray-700">
              {generatedUrl.replace(`${FAISS_BASE_URL}/`, `${MARKETPLACE_BASE_URL}/register?solution_id=aws-solution-finder-001&`)}
            </div>
            <button
              onClick={() => copyToClipboard(generatedUrl.replace(`${FAISS_BASE_URL}/`, `${MARKETPLACE_BASE_URL}/register?solution_id=aws-solution-finder-001&`))}
              className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors text-sm font-medium flex items-center justify-center"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Secondary Link
            </button>
            <p className="text-xs text-purple-600 mt-2">
              💡 Use this in post comments or for warm audiences who already know the product
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
